/**
 * One authorized POST to the TYPO3 proxy (shared by FilesService and
 * CompareService): session loss → pending, CSRF header + session cookie, one
 * CSRF refresh + retry, auth failures typed by assertAuthorized. Also the
 * coded 400/409 bodies both services turn into typed errors (#146).
 */

import { validateGatewayUrl } from '../app-constants';
import {
  assertAuthorized,
  getRequiredCsrfToken,
  withCsrfRetry,
  type AuthService,
} from '../auth-utils';
import { GatewayError, GatewayErrorCode } from './error-codes';
import { pendingIfSessionEnded, suspendIfSessionEnded } from '../session-ended';

export interface ProxyPostOptions {
  proxyUrl: string;
  authService: AuthService;
}

export interface ProxyPostHooks {
  /** Prefix of the NETWORK_ERROR message, e.g. 'Files'. */
  label: string;
  /** Called for a non-OK response after the auth checks; may throw a typed error. */
  onFailure?: (response: Response) => Promise<void>;
}

export type ProxyRequestBuilder = (csrf: string) => {
  headers: Record<string, string>;
  body: BodyInit;
};

export async function proxyPost(
  options: ProxyPostOptions,
  build: ProxyRequestBuilder,
  hooks: ProxyPostHooks
): Promise<Response> {
  const pending = pendingIfSessionEnded<Response>();
  if (pending) return pending;
  try {
    return await withCsrfRetry(options.authService, async () => {
      const { headers, body } = build(
        getRequiredCsrfToken(options.authService)
      );
      validateGatewayUrl(options.proxyUrl);
      const response = await fetch(options.proxyUrl, {
        method: 'POST',
        credentials: 'include',
        headers,
        body,
      });
      await assertAuthorized(response);
      if (!response.ok) {
        await hooks.onFailure?.(response);
        throw new GatewayError(
          `${hooks.label} request failed: ${response.status}`,
          GatewayErrorCode.NETWORK_ERROR,
          { status: response.status }
        );
      }
      return response;
    });
  } catch (error) {
    const suspended = suspendIfSessionEnded<Response>(error);
    if (suspended) return suspended;
    throw error;
  }
}

/** A 400/409 whose JSON body carries one of `codes` becomes a typed error (DUPLICATE_NAME keeps the existing item). */
export async function throwIfCoded(
  response: Response,
  codes: Record<string, GatewayErrorCode>,
  label: string
): Promise<void> {
  type Body = { code?: unknown; existing?: unknown } | null;
  let body: Body;
  try {
    body = (await response.json()) as Body;
  } catch {
    return;
  }
  const code = typeof body?.code === 'string' ? codes[body.code] : undefined;
  if (!code) return;
  if (code === GatewayErrorCode.DUPLICATE_NAME) {
    throw duplicateNameError(body?.existing);
  }
  throw new GatewayError(`${label} request failed: ${body?.code}`, code, {
    status: response.status,
  });
}

/** The DUPLICATE_NAME error; carries `existing` only when it is a usable item. */
export function duplicateNameError(existing: unknown): GatewayError {
  const item = existing as { id?: unknown } | null | undefined;
  const usable =
    typeof item === 'object' && item !== null && typeof item.id === 'string';
  return new GatewayError(
    'A document with this name already exists',
    GatewayErrorCode.DUPLICATE_NAME,
    usable ? { status: 409, existing } : { status: 409 }
  );
}
